// Routes JS supports 25 intermediates in addition to origin and destination.
// https://developers.google.com/maps/documentation/javascript/routes/intermediate-waypoints
const MAX_LEGS = 26

export function routePoint(position) {
  if (!position || !Number.isFinite(position.latitude) || !Number.isFinite(position.longitude)) return null
  if (Math.abs(position.latitude) > 90 || Math.abs(position.longitude) > 180) return null
  return { lat: position.latitude, lng: position.longitude }
}

export function routeRequests(depot, stops) {
  const points = [routePoint(depot), ...stops.map(stop => routePoint(stop.position))]
  if (points.some(point => !point)) throw new Error("MISSING_COORDINATES")
  // Consecutive stops at the same entrance need no extra driving leg. Visits to
  // the same address later in the run must still remain in the route.
  const distinct = points.filter((point, i) => !i || point.lat !== points[i - 1].lat || point.lng !== points[i - 1].lng)
  const requests = []
  for (let start = 0; start < distinct.length - 1; start += MAX_LEGS) {
    const section = distinct.slice(start, start + MAX_LEGS + 1)
    requests.push({
      origin: section[0], destination: section.at(-1),
      intermediates: section.slice(1, -1).map(location => ({ location, vehicleStopover: true })),
      travelMode: "DRIVING", routingPreference: "TRAFFIC_UNAWARE", optimizeWaypointOrder: false,
      fields: ["path", "distanceMeters", "durationMillis", "legs"]
    })
  }
  return requests
}

export function routeErrorMessage(error) {
  const code = `${error?.code || ""} ${error?.message || ""}`
  const project = routeProject(error)
  if (project) return `Routes API is disabled in Google Cloud project ${project}. Enable it there, then retry after a few minutes.`
  if (/PERMISSION_DENIED|REQUEST_DENIED|API_KEY|not.authorized|not.enabled|billing/i.test(code)) return "Enable Routes API and allow it for this Google Maps key."
  if (/RESOURCE_EXHAUSTED|OVER_QUERY_LIMIT|quota|429/i.test(code)) return "Google routing limit reached. Try again shortly."
  if (/ZERO_RESULTS|NOT_FOUND/i.test(code)) return "No drivable route found. Check the depot and stop pins."
  if (/TIMEOUT/i.test(code)) return "Google routing took too long. Try again."
  return "Could not load road routes. Try again."
}

function routeProject(error) {
  return /Routes API.*?project (\d{6,30})\b/i.exec(error?.message || "")?.[1]
}

export function routeSetupUrl(error) {
  const project = routeProject(error)
  return `https://console.cloud.google.com/apis/library/routes.googleapis.com${project ? `?project=${project}` : ""}`
}

function withTimeout(promise, timeout) {
  let timer
  return Promise.race([promise, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("TIMEOUT")), timeout) })]).finally(() => clearTimeout(timer))
}

function untilAborted(promise, signal) {
  if (!signal) return promise
  signal.throwIfAborted()
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason)
    signal.addEventListener("abort", abort, { once: true })
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort))
  })
}

// In-memory only, scoped to one workspace. Search, paging, Undo and switching run
// tabs reuse identical requests; changed coordinates or stop order get new ones.
export class ShipmentRunRoutes {
  constructor(loadLibrary = () => google.maps.importLibrary("routes"), timeout = 20000) {
    this.loadLibrary = loadLibrary
    this.timeout = timeout
    this.cache = new Map()
  }

  request(request) {
    const key = JSON.stringify(request)
    if (!this.cache.has(key)) {
      const pending = withTimeout(Promise.resolve().then(() => this.loadLibrary()).then(({ Route }) => Route.computeRoutes(request)), this.timeout).then(result => {
        const route = result.routes?.[0]
        if (!route?.path?.length) throw new Error("ZERO_RESULTS")
        return route
      }).catch(error => {
        if (this.cache.get(key) === pending) this.cache.delete(key)
        throw error
      })
      this.cache.set(key, pending)
      if (this.cache.size > 100) this.cache.delete(this.cache.keys().next().value)
    }
    return this.cache.get(key)
  }

  async get(depot, stops, { signal, progress = () => {} } = {}) {
    const requests = routeRequests(depot, stops)
    const segments = []
    for (const request of requests) {
      signal?.throwIfAborted()
      segments.push(await untilAborted(this.request(request), signal))
      signal?.throwIfAborted()
      progress(segments.length, requests.length)
    }
    const roadLegs = segments.flatMap((segment, index) => {
      const expected = requests[index].intermediates.length + 1
      return segment.legs?.length === expected ? segment.legs : Array(expected).fill(null)
    })
    let previous = routePoint(depot), legIndex = 0
    const legs = stops.map(stop => {
      const point = routePoint(stop.position)
      const repeated = previous.lat === point.lat && previous.lng === point.lng
      previous = point
      if (repeated) return { distanceMeters: 0, durationMillis: 0 }
      const leg = roadLegs[legIndex++]
      return { distanceMeters: leg?.distanceMeters ?? null, durationMillis: leg?.durationMillis ?? null }
    })
    return {
      segments, legs,
      distanceMeters: segments.every(segment => Number.isFinite(segment.distanceMeters)) ? segments.reduce((total, segment) => total + segment.distanceMeters, 0) : null,
      durationMillis: segments.every(segment => Number.isFinite(segment.durationMillis)) ? segments.reduce((total, segment) => total + segment.durationMillis, 0) : null
    }
  }
}
